<template>
  <div>
    <div class="form-container ou-donation-form">
      <div class="holder">
        <PaymentForm class="form-horizontal">
          <FormSection title="Sponsorship Options">
            <FormRow>
              <div class="sponsorship__list">
                <div class="amount-row">
                  <input
                    id="oneday"
                    type="radio"
                    class="amount"
                    @click="selectedAmount = 100"
                    :checked="selectedAmount == 100"
                  />
                  <FormLabel for="oneday">
                    <MoneyFormat
                      class="label-amount"
                      :value="100"
                      :currencyCode="form.currency || 'USD'"
                      :hide-subunits="true"
                    />
                    <span class="label-description"
                      >Co-sponsor the Tehillim<br />
                      Call for <strong>One Day</strong></span
                    >
                  </FormLabel>
                </div>
                <div class="amount-row">
                  <input
                    id="oneweek"
                    type="radio"
                    class="amount"
                    @click="selectedAmount = 360"
                    :checked="selectedAmount == 360"
                  />
                  <FormLabel for="oneweek">
                    <MoneyFormat
                      class="label-amount"
                      :value="360"
                      :currencyCode="form.currency || 'USD'"
                      :hide-subunits="true"
                    />
                    <span class="label-description"
                      >Co-sponsor the Tehillim<br />
                      Call for <strong>One Week</strong></span
                    >
                  </FormLabel>
                </div>
                <div class="amount-row">
                  <input
                    id="onemonth"
                    type="radio"
                    class="amount"
                    @click="selectedAmount = 1000"
                    :checked="selectedAmount == 1000"
                  />
                  <FormLabel for="onemonth">
                    <MoneyFormat
                      class="label-amount"
                      :value="1000"
                      :currencyCode="form.currency || 'USD'"
                      :hide-subunits="true"
                    />
                    <span class="label-description"
                      >Co-sponsor the Tehillim<br />
                      Call for <strong>One Month</strong></span
                    >
                  </FormLabel>
                </div>
              </div>

              <ErrorLabel field="amount" />
            </FormRow>
          </FormSection>

          <FormSection title="Personal Information">
            <FormRow inline>
              <FormCol field="title" :flex="1" />
              <FormCol field="firstName" :flex="2" />
              <FormCol field="lastName" :flex="2" />
            </FormRow>
            <FormRow inline>
              <FormCol field="email" />
              <FormCol field="phone" />
            </FormRow>
            <FormRow inline>
                      <Consent privacy="https://www.ou.org/privacy-policy/"/>
                  </FormRow> 
            <FormRow inline>
              <FormCol field="custom.startingDate" width="half" :showLabel="false">
                <label class="control-label">Start date of sponsorship </label>
                <input
                  type="date"
                  class="date-input"
                  @change="setDailyDate(new Date($event.target.value))"
                />
              </FormCol>
            </FormRow>
            <FormRow inline>
              <FormCol field="custom.listingName" width="full" :showLabel="false">
                <label class="control-label"
                  >How would you like your name listed in the Tehillim email?
                  <i>If anonymous, please indicate here</i>
                </label>

                <FormInput type="text" />
              </FormCol>
            </FormRow>
            <FormRow inline>
              <FormCol field="custom.dedication" width="full" :showLabel="false">
                <label class="control-label">Co-sponsorship dedication message </label>
                <FormInput type="textarea" />
              </FormCol>
            </FormRow>
          </FormSection>
          <FormSection title="Billing Information">
            <DonorAddress />
          </FormSection>
          <FormSection title="Payment Information" last>
            <FormRow>
              <Receipt />
            </FormRow>               
            <FormRow inline>
              <FormCol field="card" :showLabel="false" />
            </FormRow>
          </FormSection>
          <PaymentButton showAmount btnText="Submit" />
        </PaymentForm>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  nextTick
} from "vue";
import { usePaymentForm } from "~/shared/use-payment-form";

import { queryParams } from "~/shared/query-params";

export default defineComponent({
  props: {},
  setup() {
    const { setValue, getValue, form } = usePaymentForm();
    const params = queryParams();
    const selectedAmount = ref();
    const selectedDescription = ref();
    const customAmount = ref(form.value.amount);
    const customSelected = ref(false);
    onMounted(() => {
      if (params.type) {
        //@ts-ignore
        switch (params.type) {
          case "1000":
            setValue("amount", "1000");
            selectedAmount.value = 1000;
            break;
          case "360":
            setValue("amount", "360");
            selectedAmount.value = 360;
            break;
          case "100":
            setValue("amount", "100");
            selectedAmount.value = 100;
            break;
          default:
            setValue("amount", "100");
            selectedAmount.value = 100;
        }
      } else {
        setValue("amount", "100");
        selectedAmount.value = 100;
      }
    });

    // Use flush: 'sync' and guards to prevent infinite recursion
    let isUpdating = false;

    watch(selectedAmount, (selectedAmount) => {
      if (isUpdating) return;
      isUpdating = true;

      if (selectedAmount) {
        customSelected.value = false;
        customAmount.value = null;
        setValue("amount", "" + selectedAmount);
      }

      isUpdating = false;
    });

    watch(customAmount, async (customAmount) => {
      if (isUpdating) return;
      isUpdating = true;

      if (customAmount) {
        selectedAmount.value = null;
        customSelected.value = true;
        await nextTick();
        setValue("amount", customAmount);
      }

      isUpdating = false;
    });

    function setDailyDate(date: Date) {
      setValue(
        "custom.startingDate",
        new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        ).toISOString()
      );
      setValue(
        "custom.endingDate",
        new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 1)
        ).toISOString()
      );
    }

    return {
      form,
      selectedAmount,
      customAmount,
      customSelected,
      selectedDescription,
      currency: computed(() => form.value.currency),
      getValue,
      setValue,
      setDailyDate,
    };
  },
});
</script>
<style scoped lang="scss">
::v-deep.form-container.ou-donation-form {
  padding: 0px;
  margin: 0px;
  box-shadow: none;
  border: none;
  max-width: 100%;
}
::v-deep h2 {
  font-family: "Proxima Nova" !important;
  font-style: normal;
  font-weight: 700 !important;
  font-size: 16px !important;
  margin-bottom: 16px !important;
  line-height: 24px !important;
  color: #080f16 !important;
}
::v-deep hr {
  display: none;
  margin: 0px;
}
h2.payment-heading {
  display: flex;
  justify-content: space-between;
}
.card-img {
  display: flex;
  img {
    height: 23px;
  }
}
::v-deep .radio-inline {
  margin-left: 20px !important;
}

::v-deep .control-label {
  flex: 1;
  max-width: 100%;
  padding-top: 3px;
  line-height: normal;
  padding-bottom: 3px;
  display: block;
  font-size: 14px;
  color: #222;
}
.sponsorship__list {
  display: flex;
  gap: 16px;
  width: 100%;

  @media only screen and (max-width: 500px) {
    flex-flow: column;
  }
}
.amount-row {
  width: 100%;
  flex: 1;

  label {
    cursor: pointer;
    height: 100%;
    width: 100%;
    padding: 20px 24px;
    background: #ffffff;
    border: 2px solid #ebebeb;
    border-radius: 8px;
    &:hover {
      background: #fafafa;
    }
    @media only screen and (max-width: 500px) {
      padding: 10px 14px;
    }
  }
  input {
    height: 0;
    top: 20px;
    position: absolute;
    left: 20px;
    z-index: -1;
    visibility: hidden;
  }

  input[type="radio"]:checked + label {
    border: 2px solid #1e50b4;
    .money_format {
      color: #1e50b4;
    }
    .label-description {
      color: #080f16;
    }
  }
}
.money_format {
  font-family: "Proxima Nova";
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  margin-bottom: 8px;
  color: #080f16;
}
.label-description {
  font-family: "Proxima Nova";
  font-style: italic;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  color: #595c5f;
}
.date-input {
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #222;
}
</style>
